import React, { useState, useEffect } from 'react';
import { Save, Shield, Key, Sliders, Activity, Lock, Lightbulb, Send, Wallet, Copy, CheckCircle, Mail, RefreshCcw, Crown, ShieldAlert, TrendingUp, Layers } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../api/api';

// Extended settings interface
export interface RiskSettings {
  riskPercent: number;
  leverage: number;
  stopLoss: number;
  takeProfit: number;
  maxDailyLoss: number;
  minVolume: number;
  maxBalanceUsage: number;
  maxConcurrentTrades: number;
  isAdvisor: boolean;
  ignoreFgi: boolean;
  telegramChatId: string;
  // --- NEW FEATURES ---
  isDcaEnabled?: boolean;
  dcaMultiplier?: number;
  dcaStep?: number;
  maxDcaOrders?: number;
  isTrailingEnabled?: boolean;
  trailingCallback?: number;
  trailingActivation?: number;
  [key: string]: any;
}

interface UserProfileProps {
  riskSettings: RiskSettings;
  setRiskSettings: React.Dispatch<React.SetStateAction<RiskSettings>> | ((settings: RiskSettings) => void);
  addLog: (msg: string) => void;
  userData?: any; 
}

export const UserProfile: React.FC<UserProfileProps> = ({ 
  riskSettings, 
  setRiskSettings, 
  addLog, 
  userData 
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [keys, setKeys] = useState({ apiKey: '', apiSecret: '' });
  const [copied, setCopied] = useState(false);
  
  const isFreePlan = userData?.plan === 'FREE' || !userData?.plan;

  const [localSettings, setLocalSettings] = useState<RiskSettings>({
    riskPercent: 1,
    leverage: 5,
    stopLoss: 2,
    takeProfit: 6,
    maxDailyLoss: 3,
    minVolume: 1500000,
    maxBalanceUsage: 100,
    maxConcurrentTrades: 3,
    isAdvisor: false,
    ignoreFgi: false,
    telegramChatId: '',
    isDcaEnabled: false,
    dcaMultiplier: 1.5,
    dcaStep: 2.0,
    maxDcaOrders: 3,
    isTrailingEnabled: false,
    trailingCallback: 0.5,
    trailingActivation: 1.0
  });

  const [loading, setLoading] = useState(false);
  const [showKeys, setShowKeys] = useState(false);

  // ✅ ИСПРАВЛЕНИЕ 1: Безопасная инициализация
  useEffect(() => {
    if (riskSettings) {
      setLocalSettings((prev) => ({
        ...prev,
        // Явно мапим поля, чтобы избежать мусора
        riskPercent: Number(riskSettings.riskPercent) || 1,
        leverage: Number(riskSettings.leverage) || 5,
        stopLoss: Number(riskSettings.stopLoss) || 2,
        takeProfit: Number(riskSettings.takeProfit) || 6,
        maxDailyLoss: Number(riskSettings.maxDailyLoss) || 3,
        minVolume: Number(riskSettings.minVolume) || 1500000,
        maxBalanceUsage: Number(riskSettings.maxBalanceUsage) || 100,
        maxConcurrentTrades: Number(riskSettings.maxConcurrentTrades) || 3,
        isAdvisor: Boolean(riskSettings.isAdvisor),
        ignoreFgi: Boolean(riskSettings.ignoreFgi), // Важно для Danger Zone
        telegramChatId: riskSettings.telegramChatId || '',
        
        isDcaEnabled: Boolean(riskSettings.isDcaEnabled),
        dcaMultiplier: Number(riskSettings.dcaMultiplier) || 1.5,
        dcaStep: Number(riskSettings.dcaStep) || 2.0,
        maxDcaOrders: Number(riskSettings.maxDcaOrders) || 3,
        
        isTrailingEnabled: Boolean(riskSettings.isTrailingEnabled),
        trailingCallback: Number(riskSettings.trailingCallback) || 0.5,
        trailingActivation: Number(riskSettings.trailingActivation) || 1.0
      }));
    }
  }, [riskSettings]);

  const handleChange = (field: string, value: string | number | boolean) => {
    setLocalSettings((prev) => ({ ...prev, [field]: value }));
  };

  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(`/confirm ${code}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleResetBalance = async () => {
    if (window.confirm('⚠️ Reset Demo Balance to $10,000?')) {
        try {
            await api.post('/users/reset-paper-balance');
            addLog("♻️ Demo Balance Reset");
            window.location.reload(); 
        } catch (e) {
            addLog("❌ Error resetting balance");
        }
    }
  };

  // ✅ ИСПРАВЛЕНИЕ 2: Чистая отправка данных (Fix 400 Bad Request)
  const handleSaveSettings = async () => {
    setLoading(true);
    try {
      // Создаем "чистый" объект (DTO), чтобы не отправлять лишнее
      const payload = {
          riskPercent: Number(localSettings.riskPercent),
          leverage: Number(localSettings.leverage),
          stopLoss: Number(localSettings.stopLoss),
          takeProfit: Number(localSettings.takeProfit),
          maxDailyLoss: Number(localSettings.maxDailyLoss),
          minVolume: Number(localSettings.minVolume),
          maxBalanceUsage: Number(localSettings.maxBalanceUsage),
          maxConcurrentTrades: Number(localSettings.maxConcurrentTrades),
          isAdvisor: Boolean(localSettings.isAdvisor),
          ignoreFgi: Boolean(localSettings.ignoreFgi), // Явное приведение к Boolean
          telegramChatId: localSettings.telegramChatId,
          
          isDcaEnabled: Boolean(localSettings.isDcaEnabled),
          dcaMultiplier: Number(localSettings.dcaMultiplier),
          dcaStep: Number(localSettings.dcaStep),
          maxDcaOrders: Number(localSettings.maxDcaOrders),
          
          isTrailingEnabled: Boolean(localSettings.isTrailingEnabled),
          trailingCallback: Number(localSettings.trailingCallback),
          trailingActivation: Number(localSettings.trailingActivation)
      };

      // console.log("Sending payload:", payload); // Для отладки

      await api.post('/users/risk-settings', payload);
      
      if (!isFreePlan && keys.apiKey && keys.apiSecret) {
        await api.post('/users/settings', { apiKey: keys.apiKey, apiSecret: keys.apiSecret });
        setKeys({ apiKey: '', apiSecret: '' });
      }

      if (typeof setRiskSettings === 'function') {
          // @ts-ignore
          setRiskSettings(localSettings);
      }
      
      addLog(`✅ ${t('platform.apiSettings.success')}`);
    } catch (e: any) {
      console.error("Save error:", e.response?.data || e);
      addLog(`❌ Error saving: ${e.response?.data?.message || 'Bad Request'}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="animate-in fade-in duration-500 pb-20">
      
      {/* HEADER */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
            <h2 className="text-3xl font-black text-white tracking-tight flex items-center gap-3 uppercase italic">
            <Sliders className="text-blue-500" /> Account & Strategy
            </h2>
            {isFreePlan && (
                <div className="flex items-center gap-2 mt-2 text-orange-400 text-sm font-bold bg-orange-400/10 px-3 py-1 rounded-lg w-fit border border-orange-400/20 animate-in slide-in-from-left-2">
                    <Lock size={14} /> DEMO MODE (Paper Trading Only)
                </div>
            )}
        </div>
        
        <div className="flex gap-4">
            <button 
                onClick={handleResetBalance}
                className="flex items-center gap-2 px-4 py-2 bg-slate-800/80 border border-slate-700 hover:border-blue-500/50 hover:bg-blue-500/10 text-slate-400 hover:text-blue-400 rounded-2xl transition-all shadow-lg text-xs font-bold uppercase"
                title="Reset Demo Balance"
            >
                <RefreshCcw size={14} /> Reset Demo
            </button>

            <div className="flex items-center gap-3 px-5 py-2 bg-slate-800/50 border border-slate-700 rounded-2xl shadow-lg">
                <div className="p-1.5 bg-blue-500/20 rounded-full">
                    <Mail size={16} className="text-blue-400" />
                </div>
                <div>
                    <p className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">Logged as</p>
                    <p className="text-xs font-bold text-white">{userData?.email || 'User'}</p>
                </div>
            </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        
        <div className="space-y-8">
            {/* 1. RISKS MANAGEMENT */}
            <div className="bg-[#1e293b] border border-slate-800 rounded-[32px] p-8 shadow-xl relative overflow-hidden">
                <div className="absolute top-0 right-0 p-4 opacity-10">
                    <Shield size={120} />
                </div>
                
                <h3 className="text-xl font-black text-white mb-6 flex items-center gap-2 uppercase italic tracking-widest">
                    <Activity className="text-green-400" size={20} /> {t('platform.risk.title')}
                </h3>

                <div className="space-y-8 relative z-10">
                    {/* Max Balance Usage */}
                    <div className="bg-slate-900/50 p-5 rounded-2xl border border-slate-700/50">
                        <div className="flex justify-between mb-3">
                            <label className="text-xs font-black text-slate-500 uppercase tracking-widest flex items-center gap-2">
                                <Wallet size={14} className="text-blue-400"/> Max Balance Usage
                            </label>
                            <span className="text-white font-mono font-bold">{localSettings.maxBalanceUsage}%</span>
                        </div>
                        <input 
                            type="range" min="10" max="100" step="10"
                            value={localSettings.maxBalanceUsage}
                            onChange={(e) => handleChange('maxBalanceUsage', parseFloat(e.target.value))}
                            className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-blue-400"
                        />
                        <p className="text-[10px] text-slate-500 mt-2 font-medium italic">
                            {t('platform.riskInfo.balanceUsageDesc')}
                        </p>
                    </div>

                    <div className="grid grid-cols-2 gap-6">
                        <div>
                            <div className="flex justify-between mb-2">
                                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">{t('platform.risk.risk')}</label>
                                <span className="text-blue-400 font-mono font-bold text-xs">{localSettings.riskPercent}%</span>
                            </div>
                            <input 
                                type="range" min="0.1" max="5" step="0.1"
                                value={localSettings.riskPercent}
                                onChange={(e) => handleChange('riskPercent', parseFloat(e.target.value))}
                                className="w-full h-1.5 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-blue-500"
                            />
                            <p className="text-[9px] text-slate-600 mt-1">{t('platform.riskInfo.riskDesc')}</p>
                        </div>
                        <div>
                            <div className="flex justify-between mb-2">
                                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">{t('platform.risk.leverage')}</label>
                                <span className="text-purple-400 font-mono font-bold text-xs">{localSettings.leverage}x</span>
                            </div>
                            <input 
                                type="range" min="1" max="20" step="1"
                                value={localSettings.leverage}
                                onChange={(e) => handleChange('leverage', parseInt(e.target.value))}
                                className="w-full h-1.5 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-purple-500"
                            />
                            <p className="text-[9px] text-slate-600 mt-1">{t('platform.riskInfo.leverageDesc')}</p>
                        </div>
                    </div>

                    <div>
                        <div className="flex justify-between mb-2">
                            <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Max Concurrent Trades</label>
                            <span className="text-white font-mono font-bold text-xs">{localSettings.maxConcurrentTrades}</span>
                        </div>
                        <input 
                            type="range" min="1" max="10" step="1"
                            value={localSettings.maxConcurrentTrades}
                            onChange={(e) => handleChange('maxConcurrentTrades', parseInt(e.target.value))}
                            className="w-full h-1.5 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-blue-500"
                        />
                        <p className="text-[9px] text-slate-600 mt-1">{t('platform.riskInfo.concurrentDesc')}</p>
                    </div>

                    <div className="bg-red-500/5 p-4 rounded-xl border border-red-500/10">
                        <div className="flex justify-between mb-2">
                            <label className="text-[10px] font-black text-red-400 uppercase tracking-widest flex items-center gap-2">
                                Hard Stop (Daily Loss)
                            </label>
                            <span className="text-red-400 font-mono font-bold">-{localSettings.maxDailyLoss}%</span>
                        </div>
                        <input 
                            type="range" min="1" max="10" step="0.5"
                            value={localSettings.maxDailyLoss}
                            onChange={(e) => handleChange('maxDailyLoss', parseFloat(e.target.value))}
                            className="w-full h-1.5 bg-slate-800 rounded-lg appearance-none cursor-pointer accent-red-500"
                        />
                        <p className="text-[9px] text-slate-500 mt-1">{t('platform.riskInfo.dailyLossDesc')}</p>
                    </div>

                    <div className="grid grid-cols-2 gap-6">
                        <div>
                            <label className="text-[10px] font-black text-slate-500 uppercase block mb-2 tracking-widest">{t('platform.risk.sl')} (%)</label>
                            <div className="relative">
                                <input 
                                    type="number" value={localSettings.stopLoss}
                                    onChange={(e) => handleChange('stopLoss', parseFloat(e.target.value))}
                                    className="bg-slate-900/50 border border-slate-700 rounded-xl px-4 py-3 text-white font-mono w-full outline-none focus:border-red-500/50 transition-colors pl-3"
                                />
                                <div className="absolute right-3 top-3 text-red-500 text-[10px] font-black">LOSS</div>
                            </div>
                        </div>
                        <div>
                            <label className="text-[10px] font-black text-slate-500 uppercase block mb-2 tracking-widest">{t('platform.risk.tp')} (%)</label>
                            <div className="relative">
                                <input 
                                    type="number" value={localSettings.takeProfit}
                                    onChange={(e) => handleChange('takeProfit', parseFloat(e.target.value))}
                                    className="bg-slate-900/50 border border-slate-700 rounded-xl px-4 py-3 text-white font-mono w-full outline-none focus:border-green-500/50 transition-colors pl-3"
                                />
                                <div className="absolute right-3 top-3 text-green-500 text-[10px] font-black">WIN</div>
                            </div>
                        </div>
                    </div>

                    <div>
                        <label className="text-[10px] font-black text-slate-500 uppercase block mb-2 tracking-widest">Min Volume (24h USDT)</label>
                        <input 
                            type="number" 
                            value={localSettings.minVolume}
                            onChange={(e) => handleChange('minVolume', parseFloat(e.target.value))}
                            className="w-full bg-slate-900/50 border border-slate-700 rounded-xl px-4 py-3 text-white font-mono outline-none focus:border-blue-500 transition-colors text-sm"
                        />
                        <p className="text-[9px] text-slate-600 mt-1">{t('platform.riskInfo.minVolumeDesc')}</p>
                    </div>
                </div>
            </div>

            {/* 2. 🔥 ADVANCED STRATEGY (DCA & TRAILING) */}
            <div className="bg-[#1e293b] border border-slate-800 rounded-[32px] p-8 shadow-xl">
                <h3 className="text-xl font-black text-white mb-6 flex items-center gap-2 uppercase italic tracking-widest">
                    <TrendingUp className="text-purple-400" size={20} /> {t('platform.strategy.advancedTitle')}
                </h3>

                <div className="space-y-6">
                    {/* DCA Toggle Block */}
                    <div className={`p-5 rounded-2xl border transition-all ${localSettings.isDcaEnabled ? 'bg-indigo-500/10 border-indigo-500/30' : 'bg-slate-900/30 border-slate-800'}`}>
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <h4 className="text-sm font-bold text-white flex items-center gap-2">
                                    <Layers size={16} className={localSettings.isDcaEnabled ? "text-indigo-400" : "text-slate-500"} /> 
                                    {t('platform.strategy.dcaTitle')}
                                </h4>
                                <p className="text-[10px] text-slate-500 mt-0.5">{t('platform.strategy.dcaDesc')}</p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input type="checkbox" className="sr-only peer" checked={localSettings.isDcaEnabled} onChange={(e) => handleChange('isDcaEnabled', e.target.checked)} />
                                <div className="w-11 h-6 bg-slate-800 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-500"></div>
                            </label>
                        </div>

                        {localSettings.isDcaEnabled && (
                            <div className="grid grid-cols-3 gap-3 animate-in fade-in slide-in-from-top-2">
                                <div>
                                    <label className="text-[9px] font-bold text-slate-500 uppercase block mb-1">{t('platform.strategy.dcaStep')}</label>
                                    <input type="number" value={localSettings.dcaStep} onChange={(e) => handleChange('dcaStep', parseFloat(e.target.value))} className="w-full bg-slate-950 border border-indigo-500/30 rounded-lg px-2 py-2 text-white font-mono text-xs outline-none" />
                                </div>
                                <div>
                                    <label className="text-[9px] font-bold text-slate-500 uppercase block mb-1">{t('platform.strategy.dcaMult')}</label>
                                    <input type="number" value={localSettings.dcaMultiplier} onChange={(e) => handleChange('dcaMultiplier', parseFloat(e.target.value))} className="w-full bg-slate-950 border border-indigo-500/30 rounded-lg px-2 py-2 text-white font-mono text-xs outline-none" />
                                </div>
                                <div>
                                    <label className="text-[9px] font-bold text-slate-500 uppercase block mb-1">{t('platform.strategy.dcaMax')}</label>
                                    <input type="number" value={localSettings.maxDcaOrders} onChange={(e) => handleChange('maxDcaOrders', parseInt(e.target.value))} className="w-full bg-slate-950 border border-indigo-500/30 rounded-lg px-2 py-2 text-white font-mono text-xs outline-none" />
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Trailing Toggle Block */}
                    <div className={`p-5 rounded-2xl border transition-all ${localSettings.isTrailingEnabled ? 'bg-teal-500/10 border-teal-500/30' : 'bg-slate-900/30 border-slate-800'}`}>
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <h4 className="text-sm font-bold text-white flex items-center gap-2">
                                    <TrendingUp size={16} className={localSettings.isTrailingEnabled ? "text-teal-400" : "text-slate-500"} /> 
                                    {t('platform.strategy.trailingTitle')}
                                </h4>
                                <p className="text-[10px] text-slate-500 mt-0.5">{t('platform.strategy.trailingDesc')}</p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input type="checkbox" className="sr-only peer" checked={localSettings.isTrailingEnabled} onChange={(e) => handleChange('isTrailingEnabled', e.target.checked)} />
                                <div className="w-11 h-6 bg-slate-800 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-500"></div>
                            </label>
                        </div>

                        {localSettings.isTrailingEnabled && (
                            <div className="grid grid-cols-2 gap-4 animate-in fade-in slide-in-from-top-2">
                                <div>
                                    <label className="text-[9px] font-bold text-slate-500 uppercase block mb-1">{t('platform.strategy.trailingAct')}</label>
                                    <input type="number" value={localSettings.trailingActivation} onChange={(e) => handleChange('trailingActivation', parseFloat(e.target.value))} className="w-full bg-slate-950 border border-teal-500/30 rounded-lg px-3 py-2 text-white font-mono text-xs outline-none" />
                                </div>
                                <div>
                                    <label className="text-[9px] font-bold text-slate-500 uppercase block mb-1">{t('platform.strategy.trailingCall')}</label>
                                    <input type="number" value={localSettings.trailingCallback} onChange={(e) => handleChange('trailingCallback', parseFloat(e.target.value))} className="w-full bg-slate-950 border border-teal-500/30 rounded-lg px-3 py-2 text-white font-mono text-xs outline-none" />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>

        <div className="space-y-8">
          {/* TELEGRAM AUTH */}
          <div className="bg-[#1e293b] border border-slate-800 rounded-[32px] p-8 shadow-xl relative overflow-hidden">
              <h3 className="text-xl font-black text-white mb-6 flex items-center gap-2 uppercase italic tracking-widest">
                <Send className="text-sky-400" size={20} /> {t('platform.telegram.title')}
              </h3>

              {userData?.telegramChatId ? (
                <div className="flex items-center gap-4 p-5 bg-emerald-500/5 border border-emerald-500/20 rounded-2xl text-emerald-400">
                  <div className="p-2 bg-emerald-500/20 rounded-full">
                      <CheckCircle size={24} />
                  </div>
                  <div>
                    <p className="text-xs font-black uppercase tracking-widest">Status: Connected</p>
                    <p className="text-sm font-mono opacity-80 mt-1">Chat ID: {userData.telegramChatId}</p>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-xs text-slate-400 leading-relaxed font-medium">
                    {t('platform.telegram.step1')} <span className="text-white font-bold">@pro_tradings_bot</span> {t('platform.telegram.step2')}
                  </p>
                  <div className="bg-slate-900 p-3 rounded-xl border border-slate-700 text-center">
                      <code className="text-sky-400 font-mono text-sm">/login {userData?.email}</code>
                  </div>
                  
                  {userData?.telegramCode ? (
                    <div className="bg-sky-500/10 border border-sky-500/30 p-5 rounded-2xl flex items-center justify-between animate-in zoom-in duration-300 mt-4">
                      <div>
                        <p className="text-[9px] text-sky-400 font-black uppercase tracking-widest mb-1">{t('platform.telegram.codeLabel')}</p>
                        <p className="text-4xl text-white font-mono font-black tracking-[0.2em]">{userData.telegramCode}</p>
                      </div>
                      <button 
                        onClick={() => handleCopyCode(userData.telegramCode)}
                        className={`p-4 rounded-xl transition-all shadow-lg ${copied ? 'bg-emerald-500 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'}`}
                        title={t('platform.telegram.copy')}
                      >
                        {copied ? <CheckCircle size={24} /> : <Copy size={24} />}
                      </button>
                    </div>
                  ) : (
                    <div className="py-6 border-2 border-dashed border-slate-800 rounded-2xl text-center mt-4">
                      <p className="text-[10px] text-slate-600 font-black uppercase tracking-widest italic">{t('platform.telegram.waiting')}</p>
                    </div>
                  )}
                </div>
              )}
          </div>

          {/* API KEYS */}
          <div className="relative bg-[#1e293b] border border-slate-800 rounded-[32px] p-8 shadow-xl overflow-hidden group">
            {isFreePlan && (
                <div className="absolute inset-0 bg-[#1e293b]/80 backdrop-blur-sm z-20 flex flex-col items-center justify-center text-center p-6 transition-all">
                    <div className="bg-gradient-to-br from-yellow-400 to-orange-500 p-4 rounded-full shadow-2xl mb-4 animate-bounce">
                        <Crown size={32} className="text-white" fill="currentColor" />
                    </div>
                    <h3 className="text-2xl font-black text-white mb-2">PRO FEATURE</h3>
                    <p className="text-slate-300 text-sm mb-6 max-w-xs">{t('platform.dashboard.paperDescFree')}</p>
                    <button 
                        onClick={() => navigate('/billing')}
                        className="px-8 py-3 bg-white text-slate-900 font-bold rounded-xl hover:scale-105 transition-transform shadow-lg"
                    >
                        Upgrade Plan
                    </button>
                </div>
            )}

            <h3 className="text-xl font-black text-white mb-6 flex items-center gap-2 uppercase italic tracking-widest">
              <Key className="text-yellow-500" size={20} /> {t('platform.apiSettings.keysTitle')}
            </h3>
            <div className={`space-y-4 ${isFreePlan ? 'opacity-20 pointer-events-none' : ''}`}>
              <input 
                type="text" 
                placeholder={t('platform.apiSettings.apiKey')} 
                value={keys.apiKey}
                onChange={(e) => setKeys({...keys, apiKey: e.target.value})}
                className="w-full bg-slate-900/50 border border-slate-700 rounded-xl px-4 py-4 text-white font-mono text-sm focus:border-yellow-500 outline-none transition-all placeholder:text-slate-600"
              />
              <div className="relative">
                <input 
                  type={showKeys ? "text" : "password"} 
                  placeholder={t('platform.apiSettings.apiSecret')} 
                  value={keys.apiSecret}
                  onChange={(e) => setKeys({...keys, apiSecret: e.target.value})}
                  className="w-full bg-slate-900/50 border border-slate-700 rounded-xl px-4 py-4 text-white font-mono text-sm focus:border-yellow-500 outline-none pr-12 transition-all placeholder:text-slate-600"
                />
                <button onClick={() => setShowKeys(!showKeys)} className="absolute right-4 top-4 text-slate-500 hover:text-white transition-colors">
                  {showKeys ? <Activity size={18} /> : <Lock size={18} />}
                </button>
              </div>
            </div>
          </div>

          {/* MODE & ADVISOR */}
          <div className="bg-[#1e293b] border border-slate-800 rounded-[32px] p-8 shadow-xl">
            <h3 className="text-xl font-black text-white mb-6 flex items-center gap-2 uppercase italic tracking-widest">
                <Lightbulb className="text-yellow-400" size={20} /> Modes
            </h3>

            <div className="flex items-center justify-between mb-4">
                <div>
                    <h4 className="text-white font-bold mb-1">Advisor Mode (No Trade)</h4>
                    <p className="text-xs text-slate-500">Only Telegram signals</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                <input 
                    type="checkbox" 
                    className="sr-only peer"
                    checked={localSettings.isAdvisor}
                    onChange={(e) => handleChange('isAdvisor', e.target.checked)}
                />
                <div className="w-14 h-7 bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-yellow-500"></div>
                </label>
            </div>
          </div>

          {/* 🔥 DANGER ZONE: IGNORE FGI */}
          <div className="bg-red-500/10 border border-red-500/30 p-6 rounded-[24px] mb-6 shadow-inner">
            <h4 className="text-red-400 font-bold text-sm uppercase flex items-center gap-2 mb-3 tracking-widest">
                <ShieldAlert size={18} /> Danger Zone
            </h4>
            
            <div className="flex items-center justify-between">
                <div>
                    <p className="text-white font-bold text-sm">Ignore Fear & Greed Index</p>
                    <p className="text-[10px] text-slate-400 mt-1 max-w-xs leading-relaxed">
                        Trading continues even if Market FGI is &lt; 20 (Extreme Fear). 
                        <span className="text-red-400 font-bold block mt-1">⚠️ High risk of catching falling knives!</span>
                    </p>
                </div>
                
                <label className="relative inline-flex items-center cursor-pointer">
                    <input 
                        type="checkbox" 
                        className="sr-only peer"
                        checked={localSettings.ignoreFgi}
                        onChange={(e) => handleChange('ignoreFgi', e.target.checked)}
                    />
                    <div className="w-11 h-6 bg-slate-800 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-500 shadow-inner"></div>
                </label>
            </div>
          </div>

          {/* SAVE BUTTON */}
          <button 
            onClick={handleSaveSettings}
            disabled={loading}
            className="w-full py-5 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 rounded-2xl text-white font-black text-lg shadow-2xl transition-all flex items-center justify-center gap-3 active:scale-[0.98]"
          >
            {loading ? <Activity className="animate-spin" /> : <><Save size={24} /> {t('platform.apiSettings.saveBtn')}</>}
          </button>
        </div>

      </div>
    </div>
  );
};